import { useEffect, useCallback } from 'react';
import { BarChart3 } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { palette, transition, admin } from '../styles/tokens';
import { useAuthStore } from '../stores/authStore';
import { useReports } from '../hooks/useReports';
import { Card } from '../components/common/Card';
import { EmptyState } from '../components/common/EmptyState';
import { ExportMenu } from '../components/common/ExportMenu';
import { Skeleton } from '../components/common/Skeleton';
import { supabase } from '../lib/supabase';
import { exportLedgerCSV } from '../utils/exportLedgerCSV';
import type { ReportRange } from '../types';

const RANGES: { key: ReportRange; label: string }[] = [
  { key: 'week', label: 'This Week' },
  { key: 'month', label: 'This Month' },
  { key: 'quarter', label: 'This Quarter' },
  { key: 'year', label: 'This Year' },
];

const TH: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, color: admin.caption, textTransform: 'uppercase',
  letterSpacing: '0.06em', padding: '10px 12px', textAlign: 'left',
  borderBottom: `1px solid ${admin.border}`, whiteSpace: 'nowrap',
};
const TD: React.CSSProperties = {
  fontSize: 13, color: palette.g700, padding: '10px 12px',
  borderBottom: `1px solid ${palette.g100}`, whiteSpace: 'nowrap',
};

function StatNumber({ value, label, sub }: { value: string; label: string; sub?: string }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 28, fontWeight: 700, color: palette.g800, letterSpacing: '-0.5px' }}>{value}</div>
      <div style={{ fontSize: 12, color: palette.g500, marginTop: 2 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: palette.g400, marginTop: 1 }}>{sub}</div>}
    </div>
  );
}

export function ReportsPage() {
  const business = useAuthStore(s => s.business);
  const { revenue, jobStats, bookingStats, teamUtilization, range, setRange, loading, exportCSV } = useReports(business?.id);

  useEffect(() => { document.title = 'CrewBit · Reports'; }, []);

  const handleLedgerExport = useCallback(async () => {
    if (!business) return;
    const now = new Date();
    const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    const monthEnd = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-31`;
    const { data } = await supabase.from('payment_ledger')
      .select('date, direction, entity_type, description, amount, payment_method')
      .eq('business_id', business.id).gte('date', monthStart).lte('date', monthEnd).order('date');
    const label = now.toLocaleDateString('en-CA', { month: 'long', year: 'numeric' });
    exportLedgerCSV((data ?? []) as Array<{ date: string; direction: string; entity_type: string; description: string; amount: number; payment_method: string | null }>, label);
  }, [business]);

  if (loading) {
    return (
      <div>
        <Skeleton width={140} height={24} style={{ marginBottom: 24 }} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {[1, 2, 3, 4].map(i => <div key={i} style={{ padding: '20px', background: palette.white, borderRadius: admin.cardRadius, border: `1px solid ${admin.border}` }}><Skeleton width={100} height={14} style={{ marginBottom: 12 }} /><Skeleton height={140} /></div>)}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 700, color: admin.header, letterSpacing: '-0.5px' }}>Reports</div>
          <div style={{ fontSize: 13.5, color: admin.caption, marginTop: 4 }}>Revenue, jobs, bookings, and team utilization</div>
        </div>
        <ExportMenu options={[
          { label: 'Report Summary (CSV)', description: 'Revenue, jobs, bookings, utilization', onClick: exportCSV },
          { label: 'Payment Ledger (CSV)', description: 'Current month income & expenses', onClick: handleLedgerExport },
        ]} />
      </div>

      {/* Range toggle */}
      <div style={{ display: 'flex', gap: 0, background: palette.g100, borderRadius: 8, padding: 2, marginBottom: 20, width: 'fit-content' }}>
        {RANGES.map(r => (
          <button key={r.key} onClick={() => setRange(r.key)} style={{
            padding: '6px 16px', borderRadius: 5, border: 'none', cursor: 'pointer',
            fontSize: 12.5, fontWeight: 500, fontFamily: 'inherit',
            background: range === r.key ? palette.g800 : 'transparent',
            color: range === r.key ? palette.white : palette.g500,
            transition: `all ${transition.fast}`,
          }}>{r.label}</button>
        ))}
      </div>

      {/* Section 1: Revenue */}
      <Card title="Revenue" actionLabel={`$${revenue.totalRevenue.toLocaleString()}`}>
        {revenue.revenueByDay.length > 0 ? (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={revenue.revenueByDay}>
              <CartesianGrid strokeDasharray="3 3" stroke={palette.g100} />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: palette.g400 }} tickFormatter={d => { const dt = new Date(d + 'T00:00:00'); return dt.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }); }} />
              <YAxis tick={{ fontSize: 10, fill: palette.g400 }} tickFormatter={v => `$${v}`} />
              <Tooltip formatter={(v) => [`$${Number(v ?? 0).toLocaleString()}`, 'Revenue']} contentStyle={{ border: `1px solid ${palette.g150}`, borderRadius: 8, fontSize: 12 }} />
              <Line type="monotone" dataKey="amount" stroke={palette.accent} strokeWidth={2} dot={{ r: 3, fill: palette.accent }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <EmptyState icon={<BarChart3 size={22} />} title="Not enough data yet" subtitle="Reports will populate as you use CrewBit." />
        )}
      </Card>

      {/* Revenue breakdowns */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
        <Card title="Revenue by Service">
          {revenue.revenueByService.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={revenue.revenueByService} layout="vertical" margin={{ left: 80 }}>
                <XAxis type="number" tick={{ fontSize: 10, fill: palette.g400 }} tickFormatter={v => `$${v}`} />
                <YAxis type="category" dataKey="service" tick={{ fontSize: 11, fill: palette.g600 }} width={80} />
                <Tooltip formatter={(v) => [`$${v ?? 0}`, 'Revenue']} contentStyle={{ border: `1px solid ${palette.g150}`, borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="amount" fill={palette.accent} radius={[0, 4, 4, 0]} barSize={20} />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyState icon={<BarChart3 size={22} />} title="Not enough data yet" subtitle="Reports will populate as you use CrewBit." />}
        </Card>

        <Card title="Revenue by Client">
          {revenue.revenueByClient.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={revenue.revenueByClient.slice(0, 5)} layout="vertical" margin={{ left: 80 }}>
                <XAxis type="number" tick={{ fontSize: 10, fill: palette.g400 }} tickFormatter={v => `$${v}`} />
                <YAxis type="category" dataKey="client" tick={{ fontSize: 11, fill: palette.g600 }} width={80} />
                <Tooltip formatter={(v) => [`$${v ?? 0}`, 'Revenue']} contentStyle={{ border: `1px solid ${palette.g150}`, borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="amount" fill={palette.accentDeep} radius={[0, 4, 4, 0]} barSize={20} />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyState icon={<BarChart3 size={22} />} title="Not enough data yet" subtitle="Reports will populate as you use CrewBit." />}
        </Card>
      </div>

      {/* Section 2: Jobs & Bookings */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
        <Card title="Jobs">
          <div style={{ display: 'flex', justifyContent: 'space-around', padding: '16px 0' }}>
            <StatNumber value={`${jobStats.completionRate}%`} label="Completion rate" sub={`${jobStats.completed} of ${jobStats.totalJobs}`} />
            <StatNumber value={String(jobStats.totalJobs)} label="Total jobs" />
            <StatNumber value={jobStats.avgCompletionDays > 0 ? `${jobStats.avgCompletionDays}d` : '—'} label="Avg completion" />
          </div>
          {jobStats.byStatus.length > 0 && (
            <div style={{ display: 'flex', gap: 4, marginTop: 8 }}>
              {jobStats.byStatus.map(s => {
                const pct = jobStats.totalJobs > 0 ? (s.count / jobStats.totalJobs) * 100 : 0;
                const color = s.status === 'completed' ? palette.green : s.status === 'cancelled' ? palette.g300 : palette.accent;
                return pct > 0 ? <div key={s.status} title={`${s.status}: ${s.count}`} style={{ height: 8, borderRadius: 4, background: color, flex: pct }} /> : null;
              })}
            </div>
          )}
        </Card>

        <Card title="Bookings">
          <div style={{ display: 'flex', justifyContent: 'space-around', padding: '16px 0' }}>
            <StatNumber value={`${bookingStats.completionRate}%`} label="Completed" sub={`${bookingStats.completed} of ${bookingStats.totalBookings}`} />
            <StatNumber value={`${bookingStats.cancellationRate}%`} label="Cancelled" sub={`${bookingStats.cancelled}`} />
            <StatNumber value={`${bookingStats.noShowRate}%`} label="No-show" sub={`${bookingStats.noShow}`} />
          </div>
          {bookingStats.revenueFromBookings > 0 && (
            <div style={{ textAlign: 'center', fontSize: 12, color: palette.g500, marginTop: 4 }}>
              Revenue from bookings: <strong style={{ color: palette.g800 }}>${bookingStats.revenueFromBookings.toLocaleString()}</strong>
            </div>
          )}
        </Card>
      </div>

      {/* Section 3: Team Utilization */}
      <div style={{ marginTop: 16 }}>
        <Card title="Team Utilization">
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={TH}>Name</th>
                  <th style={TH}>Type</th>
                  <th style={{ ...TH, textAlign: 'right' }}>Scheduled</th>
                  <th style={{ ...TH, textAlign: 'right' }}>Available</th>
                  <th style={TH}>Utilization</th>
                  <th style={{ ...TH, textAlign: 'right' }}>Jobs</th>
                  <th style={{ ...TH, textAlign: 'right' }}>Bookings</th>
                </tr>
              </thead>
              <tbody>
                {teamUtilization.length === 0 ? (
                  <tr><td colSpan={7} style={{ padding: 0 }}><EmptyState icon={<BarChart3 size={22} />} title="Not enough data yet" subtitle="Reports will populate as you use CrewBit." /></td></tr>
                ) : teamUtilization.map(t => (
                  <tr key={t.userId} style={{ transition: `background ${transition.fast}` }}
                    onMouseEnter={e => (e.currentTarget.style.background = palette.g50)}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                    <td style={{ ...TD, fontWeight: 500, color: palette.g800 }}>{t.name}</td>
                    <td style={TD}>
                      <span style={{
                        fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 4, textTransform: 'capitalize',
                        background: t.employmentType === 'contractor' ? palette.amberWash : t.employmentType === 'operator' ? palette.blueWash : palette.greenWash,
                        color: t.employmentType === 'contractor' ? palette.amber : t.employmentType === 'operator' ? palette.blue : palette.green,
                      }}>{t.employmentType}</span>
                    </td>
                    <td style={{ ...TD, textAlign: 'right' }}>{t.scheduledHours}h</td>
                    <td style={{ ...TD, textAlign: 'right' }}>{t.availableHours}h</td>
                    <td style={{ ...TD, width: 140 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ flex: 1, height: 6, borderRadius: 3, background: palette.g100 }}>
                          <div style={{
                            height: '100%', borderRadius: 3, width: `${Math.min(t.utilizationRate, 100)}%`,
                            background: t.utilizationRate >= 90 ? palette.green : t.utilizationRate >= 50 ? palette.accent : palette.amber,
                            transition: 'width 0.3s',
                          }} />
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 500, color: palette.g700, minWidth: 32, textAlign: 'right' }}>{t.utilizationRate}%</span>
                      </div>
                    </td>
                    <td style={{ ...TD, textAlign: 'right' }}>{t.jobsCompleted}</td>
                    <td style={{ ...TD, textAlign: 'right' }}>{t.bookingsCompleted}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
